import { reportErrorToBugsnag } from '@air/utils-error';

import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import {
  type WorkspaceAgentRunFinishedEvent,
  type WorkspaceAgentRunStartedEvent,
} from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';
import { type WorkspaceAgentSessionArchivedEvent } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionArchived';
import { type WorkspaceAgentSessionUnarchivedEvent } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionUnarchived';
import { type WorkspaceAgentSessionUpdatedEvent } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionUpdate';
import { useEventSubscription } from '~/hooks/useEventSubscription';

export type UsePrivateWorkspaceAgentWssSubscriptionsParams = {
  /** Whose sessions to react to. Undefined only while the account is still loading. */
  accountId: string | undefined;
  workspaceId: string;
  onMessageAdded: (message: WorkspaceAgentMessage) => void;
  onSessionCreated: (sessionId: string) => void;
  /**
   * Fires for *every* elicitation in the workspace, not just the open session — the session's
   * `status` flips to `inputRequested` on the server, so the caller needs to invalidate the
   * sessions list for any of them. The handler filters against the active session id itself if
   * it only wants the active thread's pending list.
   */
  onElicitationRequested: (sessionId: string, elicitation: unknown) => void;
  /**
   * Same rationale as `onElicitationRequested`: fired for every responded elicitation in the
   * workspace so the caller can refresh statuses workspace-wide.
   */
  onElicitationResponded: (sessionId: string, elicitationId: string) => void;
  /**
   * Fires for every agent run that starts in the workspace — the server flips the session's
   * `status` from `idle` to `processing`, so the caller needs to invalidate the sessions list
   * so the bucket classifier re-reads the cached status.
   */
  onRunStarted: (event: WorkspaceAgentRunStartedEvent) => void;
  /**
   * Same rationale as `onRunStarted`: fires when an agent run finishes (success or error).
   * The session leaves `processing` and lands back in `idle`, so the bucket re-reads.
   */
  onRunFinished: (event: WorkspaceAgentRunFinishedEvent) => void;
  onSessionArchived: (event: WorkspaceAgentSessionArchivedEvent) => void;
  onSessionUnarchived: (event: WorkspaceAgentSessionUnarchivedEvent) => void;
  onSessionUpdated: (event: WorkspaceAgentSessionUpdatedEvent) => void;
  /**
   * Debounced messages refetch for the open session. Owned here so a new subscription cannot
   * forget it, and so provider handlers stay about applying the event rather than gap-filling.
   * Session.created / archived do not schedule — those are list lifecycle, not missed rows.
   */
  scheduleLiveSessionCatchUp: (sessionId: string) => void;
};

/**
 * Live agent output for the panel. Every subscription is workspace-wide and filtered on the
 * client rather than scoped to the active session id in the pattern: a session's very first
 * events can arrive before we know its id, and a subscription that doesn't exist yet would drop
 * them.
 */
export const usePrivateWorkspaceAgentWssSubscriptions = ({
  accountId,
  workspaceId,
  onMessageAdded,
  onSessionCreated,
  onElicitationRequested,
  onElicitationResponded,
  onRunStarted,
  onRunFinished,
  onSessionArchived,
  onSessionUnarchived,
  onSessionUpdated,
  scheduleLiveSessionCatchUp,
}: UsePrivateWorkspaceAgentWssSubscriptionsParams) => {
  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: { eventType: 'Activity.Agent.Session.Message.added', pattern: {} },
    options: {
      onUpdate: ({ data }) => {
        const { message } = data;

        onMessageAdded({
          ...message,
          workspaceId,
          content: message.content ?? null,
          metadata: message.metadata ?? null,
          isPending: false,
        });
        scheduleLiveSessionCatchUp(message.sessionId);
      },
    },
  });

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: { eventType: 'Activity.Agent.Session.created', pattern: {} },
    options: {
      onUpdate: ({ data }) => {
        const { session } = data;

        if (session.agentType !== 'workspace' || session.workspaceId !== workspaceId) {
          return;
        }

        // Whose session this is cannot be told without an account, and a session that is not the
        // user's must not be adopted. By the time one of theirs is announced they have used the
        // panel, so the account should long since have loaded.
        if (!accountId) {
          reportErrorToBugsnag({
            error: new Error('Workspace agent session announced before the account loaded'),
            context: 'usePrivateWorkspaceAgentWssSubscriptions',
            metadata: { sessionId: session.id },
          });

          return;
        }

        // Workspace-wide, so a teammate starting their own conversation is announced here too.
        // Adopting theirs would point the panel at a conversation that is not the user's, and send
        // their next message into it.
        if (session.createdBy !== accountId) {
          return;
        }

        onSessionCreated(session.id);
      },
    },
  });

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: { eventType: 'Activity.Agent.Session.Elicitation.requested', pattern: {} },
    options: {
      onUpdate: ({ data }) => {
        onElicitationRequested(data.sessionId, data.elicitation);
        scheduleLiveSessionCatchUp(data.sessionId);
      },
    },
  });

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: { eventType: 'Activity.Agent.Session.Elicitation.responded', pattern: {} },
    options: {
      onUpdate: ({ data }) => {
        onElicitationResponded(data.sessionId, data.elicitationId);
        scheduleLiveSessionCatchUp(data.sessionId);
      },
    },
  });

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: { eventType: 'Activity.Agent.Session.Run.started', pattern: {} },
    options: {
      onUpdate: ({ createdAt, data }) => {
        if (data.containerType !== 'workspace' || data.workspaceId !== workspaceId) {
          return;
        }

        onRunStarted({
          createdAt,
          runId: data.runId,
          sessionId: data.sessionId,
        });
        scheduleLiveSessionCatchUp(data.sessionId);
      },
    },
  });

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: { eventType: 'Activity.Agent.Session.Run.finished', pattern: {} },
    options: {
      onUpdate: ({ createdAt, data }) => {
        if (data.containerType !== 'workspace' || data.workspaceId !== workspaceId) {
          return;
        }

        onRunFinished({
          createdAt,
          outcome: data.outcome,
          runId: data.runId,
          sessionId: data.sessionId,
        });
        scheduleLiveSessionCatchUp(data.sessionId);
      },
    },
  });

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: accountId
      ? {
          eventType: 'Activity.Agent.Session.archived',
          pattern: { sessionCreatedBy: accountId },
        }
      : undefined,
    options: {
      onUpdate: ({ data }) => {
        onSessionArchived(data);
      },
    },
  });

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: accountId
      ? {
          eventType: 'Activity.Agent.Session.unarchived',
          pattern: { sessionCreatedBy: accountId },
        }
      : undefined,
    options: {
      onUpdate: ({ data }) => {
        onSessionUnarchived(data);
      },
    },
  });

  useEventSubscription({
    currentWorkspaceId: workspaceId,
    event: accountId
      ? {
          eventType: 'Activity.Agent.Session.updated',
          pattern: { sessionCreatedBy: accountId },
        }
      : undefined,
    options: {
      onUpdate: ({ data }) => {
        onSessionUpdated(data);
        scheduleLiveSessionCatchUp(data.sessionId);
      },
    },
  });
};
